import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faEnvelope, faLock, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { AuroraBackground } from '../ui/aurora-background';
import { useAuth } from '../../context/AuthContext';
import { trackFormSubmit, trackLogin } from '../../utils/analytics';
import { subscriptionAPI } from '../../services/api';
import SEO from '../SEO';
import { seoConfig } from '../../utils/seoData';
import { useTranslation } from 'react-i18next';
import NotificationService from '../../services/notificationService';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const { login, loading, business } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    trackFormSubmit('login_form');

    try {
      const loggedInBusiness = await login(formData.email, formData.password);
      trackLogin('email');
      
      // Get current language
      const currentLang = lang || i18n.language || 'tr';

      // Check if email is verified, if not redirect to verification page
      if (loggedInBusiness && !loggedInBusiness.emailVerified) {
        navigate(`/${currentLang}/verify-email`, { state: { email: formData.email, fromLogin: true } });
        return;
      }

      // Check subscription
      if (loggedInBusiness?._id) {
        try {
          const subscriptionResponse = await subscriptionAPI.getSubscription(loggedInBusiness._id);
          const subscription = subscriptionResponse.data?.subscription;
          
          // If no active subscription, redirect to checkout
          if (!subscription || subscription.status !== 'active') {
            navigate(`/${currentLang}/checkout`);
            return;
          }
        } catch (err: any) {
          // If subscription not found (404), redirect to checkout
          if (err.response?.status === 404 || !err.response) {
            navigate(`/${currentLang}/checkout`);
            return;
          }
        }
      }

      // Has subscription, go to dashboard
      navigate('/social-media');

      // Request notification permission if not already requested
      // Only ask if user hasn't been asked before and browser supports notifications
      const notificationService = NotificationService.getInstance();
      if (notificationService.isSupported()) {
        // Request notification permission asynchronously (don't block navigation)
        // Wait a bit longer to ensure service worker is ready
        setTimeout(async () => {
          try {
            console.log('[Login] Requesting notification permission...');
            const result = await notificationService.requestAndSubscribe();
            console.log('[Login] Notification permission result:', result);
          } catch (error) {
            // Silently fail - don't show error to user
            console.error('[Login] Notification permission request failed:', error);
          }
        }, 2000); // Wait 2 seconds after navigation to ensure service worker is ready
      } else {
        console.log('[Login] Push notifications not supported in this browser');
      }
    } catch (err: any) {
      setError(err.message || 'Giriş yapılırken bir hata oluştu');
    }
  };

  return (
    <>
      <SEO {...seoConfig.login} />
      <AuroraBackground className="w-full h-full overflow-scroll">
      <div className="w-full min-h-screen flex items-center justify-center relative overflow-hidden py-10">
        {/* Back Button */}
        <button
          onClick={() => {
            const currentLang = lang || i18n.language || 'tr';
            navigate(`/${currentLang}/home`);
          }}
          className="absolute top-6 left-6 z-20 flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white/80 md:hover:text-white md:hover:bg-white/20 transition-all duration-300 group"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-sm md:group-hover:-translate-x-1 transition-transform" />
        </button>

        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900"></div>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/10 rounded-full blur-xl animate-float"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-blue-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-pink-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }}></div>

        <div className="relative z-10 w-full max-w-md mx-auto px-6">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center mb-6 cursor-pointer" onClick={() => {
              const currentLang = lang || i18n.language || 'tr';
              navigate(`/${currentLang}/home`);
            }}>
              <img 
                src="/logo.svg" 
                alt="Winnomi Logo" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Hoş Geldiniz</h2>
            <p className="text-white/70">Hesabınıza giriş yapın</p>
          </div>

          {/* Login Form */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-2xl">
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                  E-posta Adresi
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faEnvelope} className="text-white/40 text-sm" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                    placeholder="ornek@email.com"
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                  Şifre
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faLock} className="text-white/40 text-sm" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/40 md:hover:text-white/60 transition-colors"
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-sm" />
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-purple-600 bg-white/5 border-white/20 rounded focus:ring-purple-500 focus:ring-2"
                  />
                  <span className="ml-2 text-sm text-white/70">Beni hatırla</span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const currentLang = lang || i18n.language || 'tr';
                    navigate(`/${currentLang}/forgot-password`);
                  }}
                  className="text-sm text-purple-400 md:hover:text-purple-300 transition-colors"
                >
                  Şifremi unuttum
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-700 py-3 px-6 rounded-xl text-white font-semibold md:hover:from-purple-600 md:hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-300 transform md:hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Giriş yapılıyor...</span>
                  </div>
                ) : (
                  'Giriş Yap'
                )}
              </button>
            </form>


            {/* Register Link */}
            <div className="mt-8 text-center">
              <p className="text-white/70">
                Hesabınız yok mu?{' '}
                <a
                  href="/register"
                  className="text-purple-400 md:hover:text-purple-300 font-semibold transition-colors"
                >
                  Kayıt olun
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuroraBackground>
    </>
  );
};

export default Login;
